import {HttpBackend, HttpClient, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError} from 'rxjs';
import {TokenService} from './token.service';
import {environment} from '../../environments/environment.dev';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly tokenService = inject(TokenService);
  // HttpBackend bypasses all interceptors — avoids circular calls on the refresh request
  private readonly directHttp = new HttpClient(inject(HttpBackend));

  private isRefreshing = false;
  private readonly refreshDone$ = new BehaviorSubject<string | null>(null);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const token = this.tokenService.token();
    const authedReq = token ? this.attachToken(req, token) : req;

    return next.handle(authedReq).pipe(
      catchError(error => {
        const isAuthUrl = req.url.includes('/Auth/Login') || req.url.includes('/Auth/RefreshToken');
        if (error.status === 401 && !isAuthUrl) {
          return this.handle401(req, next);
        }
        if (error.status === 401) {
          this.tokenService.logout();
        }
        return throwError(() => error);
      })
    );
  }

  private handle401(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      return this.refreshDone$.pipe(
        filter(t => t !== null),
        take(1),
        switchMap(token => next.handle(this.attachToken(req, token!)))
      );
    }

    const refreshToken = this.tokenService.refreshToken();
    if (!refreshToken) {
      this.tokenService.logout();
      return throwError(() => new Error('No refresh token available'));
    }

    this.isRefreshing = true;
    this.refreshDone$.next(null);

    return this.directHttp.post<{ token: string; refreshToken: string }>(
      `${environment.apiUrl}/Auth/RefreshToken`,
      {refreshToken}
    ).pipe(
      switchMap(response => {
        this.isRefreshing = false;
        this.tokenService.updateTokens(response.token, response.refreshToken);
        this.refreshDone$.next(response.token);
        return next.handle(this.attachToken(req, response.token));
      }),
      catchError(err => {
        this.isRefreshing = false;
        this.tokenService.logout();
        return throwError(() => err);
      })
    );
  }

  private attachToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
    });
  }
}
